import _ from "lodash";
import React, { useRef, useState, useMemo } from "react";
import { connect } from "react-redux";
import { useI18n } from "Hooks";
import withLoader from "UIComponents/GqlWithLoader";
import PaginationLoader from "UIComponents/PaginationLoader";
import { prepareLoadMoreFeed, isIdEqual } from "Utils";
import { getLabelValueForMultiFilterLabelButton } from "Utils/getLabelValue";
import {
  Button,
  EmptyState,
  Modal,
  SearchInput,
  Tooltip,
  RadioButton,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import {
  NoSearchInFilterIllustration,
  NoFamilyChannelIllustration,
} from "@toddle-edu/ds-theme";
import { FamilyOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { compose, graphql } from "lib/apolloClient";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getOrganizationId } from "modules/CommonSelectors";
import { goToRelativeRoute } from "modules/Services";
import { useHandleScroll } from "Utils/CustomHooks";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getMessagingDetailsRoute,
  getSubText,
  getFormattedNameForUserType,
  isPaidConnectModule,
  getChatsAndChannelEnabledCurriculumPrograms,
} from "MessagingV2/modules/Utils";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar/Avatar";
import { createFamilyChannel } from "MessagingV2/modules/MessagingModule";
import { getMessagingStudentsWithFamilyMemberQuery } from "MessagingV2/modules/MessagingQuery";
import { STUDENT } from "Constants/stringConstants";
import classes from "./CreateFamilyChannelModal.scss";
import StudentAlerts from "AppComponents/StudentAlerts";

const customAvatarStyle = {
  size: "medium",
};

const styles = {
  loadingStyle: { width: 20, height: 20 },
  containerStyle: { width: "50px" },
  modalBodyStyle: {
    padding: "0",
    overflow: "hidden",
  },
  nextButtonStyle: { height: "48px", padding: "12px 16px" },
};

const UserTypeCard = ({
  avatarId,
  mainText,
  subText,
  profileImage,
  parents,
  selectedStudentId,
  handleSelectStudent,
  alertTypes,
}) => {
  const { t } = useI18n(["common"]);
  const onClickHandler = () => handleSelectStudent(avatarId);
  return (
    <div className={classes.userTypeCardContainer}>
      <MessagingAvatar
        id={avatarId}
        name={mainText}
        url={profileImage}
        customAvatarStyles={customAvatarStyle}
        customAvatarContainerClassName={classes.avatarContainer}
      />

      <div className={classes.userInfoContainer}>
        <div className={classes.userTitleContainer}>
          <div className={classNames("text-body", classes.title)}>
            {mainText}
          </div>
          <StudentAlerts studentId={avatarId} alertTypes={alertTypes} />
        </div>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > 678}
            type={"wrapperAsElement"}
          >
            <div className={classNames("text-body-s", classes.userSubTitle)}>
              {_.truncate(subText, { length: 678 })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      <div className={classes.checkboxContainer}>
        <div className={classes.checkbox}>
          <div className={classes.parentsIconAndCountContainer}>
            <div className={classNames("text-body-1", classes.parentsCount)}>
              {_.size(parents)}
            </div>
            <Tooltip
              placement="top"
              tooltip={
                <div className="text-tooltip">
                  {t("common:add_family_members_of_student")}
                </div>
              }
            >
              <FamilyOutlined variant="subtle" />
            </Tooltip>
          </div>
          <RadioButton
            checked={isIdEqual(selectedStudentId, avatarId)}
            disabled={_.isEmpty(parents)}
            onChange={onClickHandler}
          />
        </div>
      </div>
    </div>
  );
};

const UserList = ({
  result,
  selectedStudentId,
  setSelectedStudentId,
  loggedInUserType,
  searchText,
}) => {
  const {
    students,
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    isFetchingMore,
  } = result;
  const containerRef = useRef(null);
  const { t } = useI18n(["common"]);
  const handleSelectStudent = id => {
    setSelectedStudentId(id);
  };
  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
    hookDependencies: [containerRef.current],
  });

  if (!_.size(students) && !isFetchingMore) {
    const isSearching = !_.isEmpty(searchText);

    const emptyStateClass = classNames({
      [classes.emptyState]: !isSearching,
    });

    return (
      <div className={classes.emptyStateContainer}>
        <div className={emptyStateClass}>
          <EmptyState
            illustration={
              isSearching
                ? NoSearchInFilterIllustration
                : NoFamilyChannelIllustration
            }
            title={
              isSearching
                ? t("common:search_results_not_found")
                : t("common:no_family_members_available_yet")
            }
            subtitle={
              isSearching
                ? t("common:please_try_a_different_keyword")
                : t("common:no_family_members_available_yet_subtext")
            }
            dsVersion="2.0"
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={classes.listContainer}>
      {_.map(students, student => {
        const { id, profileImage, familyMembers, alertTypes = [] } = student;

        return (
          <UserTypeCard
            key={id}
            avatarId={id}
            selectedStudentId={selectedStudentId}
            handleSelectStudent={handleSelectStudent}
            mainText={getFormattedNameForUserType({ user: student })}
            subText={getSubText({
              loggedInUserRole: loggedInUserType,
              messagedUserObj: student,
              messagedUserRole: _.get(student, "type"),
              t,
            })}
            profileImage={profileImage}
            parents={familyMembers ?? []}
            alertTypes={alertTypes}
          />
        );
      })}
      {isFetchingMore ? <PaginationLoader /> : null}
    </div>
  );
};

const mapStateToProps = state => {
  const { user_type: loggedInUserType } = getUserInfo();
  return {
    loggedInUserType,
  };
};

const UserListWrapper = compose(
  connect(mapStateToProps, null),
  graphql(getMessagingStudentsWithFamilyMemberQuery, {
    name: "getMessagingStudentsWithFamilyMember",
    options: props => {
      const { searchText } = props;
      const curriculumProgramIds = _.get(props, "curriculumProgramIds");
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          input: {
            first: 20,
            searchText,
            includeRelatedUsersOnly: true,
            userType: [STUDENT],
            includePaidUsersOnly: isPaidConnectModule(),
            userStatus: "ACTIVE",
            ...(!_.isEmpty(curriculumProgramIds)
              ? { curriculumProgramIds }
              : {}),
          },
        },
      };
    },
    props: ({ getMessagingStudentsWithFamilyMember }) => {
      const { variables, node = {} } =
        getMessagingStudentsWithFamilyMember || {};

      const students = _.map(
        _.get(node, "messageV2Users.edges", []),
        student => student.node
      );

      const hasNextPage = _.get(
        node,
        "messageV2Users.pageInfo.hasNextPage",
        false
      );

      const result = {
        students,
        hasNextPage,
        networkStatus: getMessagingStudentsWithFamilyMember["networkStatus"],
        isFetchingMore: _.includes(
          [3],
          getMessagingStudentsWithFamilyMember["networkStatus"]
        ),
        loadMoreFeed: prepareLoadMoreFeed({
          query: getMessagingStudentsWithFamilyMemberQuery,
          variables: {
            ...variables,
            input: {
              ..._.get(variables, "input", {}),
              before: _.get(node, "messageV2Users.pageInfo.endCursor", null),
            },
          },
          queryData: getMessagingStudentsWithFamilyMember,
          key: "messageV2Users",
        }),
      };
      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes(
          [1, 2],
          getMessagingStudentsWithFamilyMember["networkStatus"]
        ),
        result,
      };
    },
  }),
  withLoader
)(UserList);

const CreateFamilyChannelModal = ({
  onCloseModal,
  createFamilyChannel,
  goToRelativeRoute,
  result,
}) => {
  const { t } = useI18n(["common"]);
  const [searchText, setSearchText] = useState();
  const [selectedStudentId, setSelectedStudentId] = useState();
  const [loading, setLoading] = useState(false);

  const curriculumOptions = useMemo(
    () =>
      _.map(getChatsAndChannelEnabledCurriculumPrograms(), program => ({
        label: program.label,
        value: program.id,
      })),
    []
  );

  const showCurriculumFilter = _.size(curriculumOptions) > 1;

  const [selectedCurriculumIds, setSelectedCurriculumIds] = useState(() =>
    _.map(curriculumOptions, "value")
  );

  const onCreate = async () => {
    if (!selectedStudentId) return;
    try {
      setLoading(true);
      const channelId = await createFamilyChannel(selectedStudentId);
      if (channelId) {
        goToRelativeRoute({
          route: getMessagingDetailsRoute({ id: channelId }),
          replacePath: "messaging",
          type: "replace",
        });
        onCloseModal();
      }
    } catch (error) {
      // do nothing
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      height={"680px"}
      width={"600px"}
      hasOverlay={true}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onClose={onCloseModal}
      isOpen={true}
      dsVersion="2.0"
    >
      <Modal.Header
        title={t("common:family_channel_modal_title")}
        isClosable={true}
        subtitle={t("common:select_student_family_member_like_to_add")}
      />

      <Modal.Body style={styles.modalBodyStyle}>
        <div className={classes.modalBodyContainer}>
          <div className={classes.searchBarContainer}>
            <SearchInput
              size="medium"
              placeholder={`${t("common:search_for_with_label", {
                label: t("common:student"),
              })} ...`}
              onChange={setSearchText}
              dsVersion="2.0"
              focusSearchBySlash={true}
            />
            {showCurriculumFilter ? (
              <div className={classes.curriculumFilterContainer}>
                <ChecklistDropdown
                  options={curriculumOptions}
                  value={selectedCurriculumIds}
                  onChange={setSelectedCurriculumIds}
                  showAllOption
                  showSelectedOptionsOnTop={false}
                  dsVersion="2.0"
                >
                  <FilterLabelButton
                    size="medium"
                    variant="neutral"
                    type="outlined"
                    label={t("common:school")}
                    labelValue={getLabelValueForMultiFilterLabelButton({
                      selectedOptions: selectedCurriculumIds,
                      options: curriculumOptions,
                      t,
                      noneText: t("common:none"),
                    })}
                    dsVersion="2.0"
                  />
                </ChecklistDropdown>
              </div>
            ) : null}
          </div>
          <UserListWrapper
            result={result}
            selectedStudentId={selectedStudentId}
            setSelectedStudentId={setSelectedStudentId}
            searchText={searchText}
            curriculumProgramIds={
              showCurriculumFilter ? selectedCurriculumIds : undefined
            }
          />
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator={true}>
        <div className={classes.footerContainer}>
          <Button
            type="outlined"
            variant="neutral"
            size="medium"
            dsVersion="2.0"
            onClick={onCloseModal}
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant="progressive"
            size="medium"
            onClick={onCreate}
            dsVersion="2.0"
            disabled={!selectedStudentId || loading}
          >
            {t("common:create")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapActionCreators = {
  createFamilyChannel,
  goToRelativeRoute,
};

export default connect(null, mapActionCreators)(CreateFamilyChannelModal);
